import { userType } from "constants-lib/authentication"
import { localStorageKey } from "constants-lib/localStorage"
import toLower from "lodash/toLower"
import toArray from "lodash/toArray"
import each from "lodash/each"
import get from "lodash/get"
import isEmpty from "lodash/isEmpty"
import mapValues from "lodash/mapValues"
import map from "lodash/map"
import join from "lodash/join"
import split from "lodash/split"
import replace from "lodash/replace"
import uniq from "lodash/uniq"
import throttle from "lodash/throttle"
import { getDeviceChannel } from "utils-lib/getDeviceChannel"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import {
  LINE_OF_BUSINESS,
  PREPAYMENT_NO,
  PREPAYMENT_YES,
} from "constants-lib/common"
import { getWindow } from "utils-lib/getWindow"
import nanoid from "nanoid"
import { getSessionStorage } from "./isBot"

const ssrWindow = getWindow()

/**
 * Returns the site section based on the current URL pathname and language.
 * @param {string} language - The language code for the current page.
 * @returns {string} - The site section name.
 */
export function getSiteSection(language) {
  const { pathname } = ssrWindow.location
  const languageRoute = getLanguageRoute(language)

  let site_section = ``

  if (pathname.indexOf(languageRoute) === -1 || pathname === languageRoute) {
    site_section = `homepage`
  } else {
    const splitBySlash = split(pathname, `/`)
    site_section =
      splitBySlash.length < 4 ? `blank` : get(splitBySlash, `[3]`, ``)
  }

  return replace(site_section, `-`, `_`)
}

/**
 * Returns the page name based on the current URL and language.
 * @param {string} language - The language code for the current page.
 * @returns {string} - The page name.
 */
export function getPagename(language) {
  const { pathname } = ssrWindow.location
  const languageRoute = getLanguageRoute(language)

  let pagename = ``

  if (pathname.indexOf(languageRoute) === -1 || pathname === languageRoute) {
    pagename = `homepage`
  } else {
    const splitBySlash = split(ssrWindow?.location.pathname, `/`)
    const length = get(splitBySlash, `length`, 0)
    pagename =
      splitBySlash.length === 0
        ? `blank`
        : get(splitBySlash, `[${length - 1}]`, ``)
  }

  return replace(pagename, `-`, `_`)
}

/**
 * Returns an array of customer account IDs linked to the given user's account.
 * @param {Object} userManageAccount - The user's account object.
 * @returns {Array} - An array of customer account IDs.
 */
export function getCustomerIds(userManageAccount) {
  return get(userManageAccount, `linkedAccounts`, ``)
    ? toArray(
        mapValues(
          get(userManageAccount, `linkedAccounts`, ``),
          `custAccountId`,
        ),
      )
    : []
}

/**
 * Returns an array of unique and sorted LOBs (Line of Business) associated with the given user's managed account.
 * @param {Object} userManageAccount - The user's managed account object.
 * @returns {(string[]|string)} - An array of unique and sorted LOBs or an empty string if no LOBs are found.
 */
export function getLOB(userManageAccount) {
  const lobs = []
  each(get(userManageAccount, `linkedAccounts`, ``), (acct) => {
    lobs.push(toLower(get(acct, `lob`)))
  })

  return !isEmpty(lobs) ? uniq(lobs).sort() : ``
}

/**
 * Returns an array of customer types based on the provided list of lines of business.
 * @param {string[]} lobList - The list of lines of business to map to customer types.
 * @returns {string[]} An array of customer types.
 */
export function getCustomerType(lobList) {
  // eslint-disable-next-line consistent-return
  return map(lobList, (lob) => {
    switch (lob) {
      case LINE_OF_BUSINESS?.LANDFILL?.toLowerCase():
        return `ln`
      case LINE_OF_BUSINESS?.rolloff:
        return `tro`
      case LINE_OF_BUSINESS?.commercial:
        return `smb`
      case LINE_OF_BUSINESS?.residential:
        return `omr`
      default:
    }
  })
}

let landedIndex

let visitorObj = {
  isRepeat: "",
  isLoggedIn: "",
  lob: "",
  wmUserId: "",
  customerType: "",
  wmCustomerId: "",
  isBot: "",
  sessionId: "",
  marketingCloudVisitorID: "",
  experienceCloudId: "",
}

export const updateVisitorObject = throttle(
  ({
    userAccount,
    userManageAccount,
    LDFFReleaseSetConsistentLRSessionId = false,
  }) => {
    const reduxUserState = get(userAccount, `userState`, ``)
    const localStorageUserState = get(
      ssrWindow,
      `localStorage.${localStorageKey?.USER_STATE}`,
    )
    const lob = getLOB(userManageAccount)
    const botdResult = JSON?.parse(getSessionStorage("botdResult"))
    let sessionId
    if (LDFFReleaseSetConsistentLRSessionId) {
      let getId = getSessionStorage("ID")
      if (!getId) {
        getId = nanoid(12)
        ssrWindow?.sessionStorage?.setItem("ID", getId)
      }
      sessionId = getId
    } else {
      const getId = getSessionStorage("ID")
      sessionId = isEmpty(getId) ? "" : getId
    }
    const customer_type = getCustomerType(lob)

    visitorObj = {
      isRepeat:
        reduxUserState === userType?.COOKIED ||
        localStorageUserState === userType?.COOKIED
          ? toLower(PREPAYMENT_YES)
          : toLower(PREPAYMENT_NO),
      isLoggedIn:
        reduxUserState === userType?.LOGGED_IN ||
        localStorageUserState === userType?.LOGGED_IN
          ? toLower(PREPAYMENT_YES)
          : toLower(PREPAYMENT_NO),
      lob: join(lob, `|`),
      wmUserId: get(userAccount, `userDetails.userId`, ``), // user profile id,
      customerType: join(customer_type, `|`),
      wmCustomerId: getCustomerIds(userManageAccount), // customer id list,
      isBot: get(botdResult, "bot", ``),
      sessionId,
      marketingCloudVisitorID: "", // Analytics team will handle this attribute
      experienceCloudId: "", // Analytics team will handle this attribute
    }

    ssrWindow?.sessionStorage?.setItem(
      localStorageKey?.VISITOR_OBJECT,
      JSON?.stringify(visitorObj),
    )

    if (ssrWindow?.adobeDataLayer && ssrWindow?.adobeDataLayer[landedIndex]) {
      ssrWindow.adobeDataLayer[landedIndex].visitor = visitorObj
    }
  },
  500,
)

// WHEN ANY PAGE LOADS UPDATES THE ACDL
export const updateGlobalPageLoad = ({
  language,
  previous_page_name = ``,
  templateName = ``,
  lobType = ``,
  gisIDs = ``,
  pageType = ``,
}) => {
  const siteSection = getSiteSection(language)
  const pageName = getPagename(language)
  const visitorStr = get(
    ssrWindow,
    `sessionStorage.${localStorageKey?.VISITOR_OBJECT}`,
  )

  landedIndex = ssrWindow?.adobeDataLayer?.length

  const tempADL = {
    event: "landed",
    eventInfo: {
      eventName: "landed",
    },
    page: {
      baseUrl: ssrWindow?.location?.origin,
      countryLanguage: toLower(language),
      device: toLower(getDeviceChannel()),
      pageName,
      pagenameConcat: `${siteSection}|${pageName}`,
      pageType,
      previousPageName: previous_page_name,
      siteSection,
      lobType,
      templateName,
      gisIds: gisIDs,
      channel: "", // requirement yet to come from client
    },
    visitor: visitorStr ? JSON?.parse(visitorStr) : visitorObj,
    landedIndex,
  }

  ssrWindow?.adobeDataLayer?.push(tempADL)
}

// WHEN A USER FAILS TO LOGIN UPDATES THE ACDL
export const updateLoggedInFailure = () => {
  setTimeout(() => {
    const failureMessage = ssrWindow?.document
      ?.querySelector(".error-credentials")
      ?.innerText?.split(".")[0]
      ? `${
          ssrWindow?.document
            ?.querySelector(".error-credentials")
            ?.innerText?.split(".")[0]
        }.`
      : `${
          ssrWindow?.document
            ?.querySelector(".error-credentials")
            ?.textContent?.split(".")[0]
        }.`
    const tempADL = {
      event: "loginFailure",
      eventInfo: {
        eventName: "loginFailure",
      },
      page: {
        failureMessage,
      },
      visitor: {
        isLoggedIn: `no`,
      },
    }
    ssrWindow?.adobeDataLayer?.push(tempADL)
  }, 2000)
}

// Generic method to push event to Adobe Data Layer
export const pushToAdobeDataLayer = (eventName, additionalData = {}) => {
  const tempADL = {
    event: eventName,
    eventInfo: {
      eventName,
    },
    ...additionalData,
  }

  ssrWindow?.adobeDataLayer?.push(tempADL)
}
